import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Airport } from "@/models/airport";
import {
  fetchAirportData,
  fetchPuzzleData,
  isPreview,
  parseAirports,
  parsePuzzle,
} from "@/services/daily";
import { loadGuessedIatas, localDateIso, progressKey } from "./game";
import { language } from "./strings";
import { stubAirports } from "./stubAirports";
import { stubPuzzle } from "./stubPuzzle";
import { DailyPuzzle } from "./types";

export type DailyData =
  | { status: "loading" }
  | { status: "error" }
  // The API was reached and has no puzzle for the player's date.
  | { status: "empty" }
  | {
      status: "ready";
      puzzle: DailyPuzzle;
      // null until the airport list has arrived.
      airports: Airport[] | null;
      // Goes up if a newer copy of the puzzle replaces the one first shown.
      version: number;
    };

// What the API last sent is remembered in the browser, so a return visit
// shows the game at once and checks for anything newer behind the scenes.
const PUZZLE_KEY = "fg_daily_cache_puzzle";
const AIRPORTS_KEY = "fg_daily_cache_airports";
// The airport list barely changes; it is asked for again once it is this old.
const AIRPORTS_FRESH_FOR = 12 * 60 * 60 * 1000;

const remembered = <T,>(key: string): T | null => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "null");
  } catch {
    return null;
  }
};

const remember = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the game still works, just without the head start.
  }
};

// Loads the day's puzzle and the airport list. The game can be shown as soon
// as the puzzle is in hand; the airports, only needed once the player starts
// typing, follow. In development only, if the API cannot be reached, the
// stubbed puzzle and airports are used.
export const useDailyData = () => {
  const [data, setData] = useState<DailyData>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const preview = isPreview();
    const today = localDateIso();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const savedPuzzle = preview ? null : remembered<{ date: string; lang?: string; data: any }>(PUZZLE_KEY);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const savedAirports = remembered<{ savedAt: number; data: any[] }>(AIRPORTS_KEY);
    // A puzzle remembered in another language is asked for again in this one.
    const lang = language();
    const puzzleData =
      savedPuzzle?.date === today && (savedPuzzle.lang ?? "en") === lang ? savedPuzzle.data : null;

    let puzzle = puzzleData ? parsePuzzle(puzzleData) : null;
    let airports = savedAirports ? parseAirports(savedAirports.data) : null;
    let version = 0;
    // Once the API has said there is no puzzle, nothing else changes that.
    let empty = false;
    const show = () => {
      if (!cancelled && puzzle) setData({ status: "ready", puzzle, airports, version });
    };
    const fail = (error: unknown) => {
      if (cancelled || empty) return;
      if (!preview && axios.isAxiosError(error) && error.response?.status === 404) {
        empty = true;
        setData({ status: "empty" });
      } else if (import.meta.env.DEV && !preview) {
        console.warn("Daily Service: API unavailable, using stubbed data.", error);
        setData({ status: "ready", puzzle: stubPuzzle, airports: stubAirports, version: 0 });
      } else {
        setData({ status: "error" });
      }
    };

    if (puzzle) show();
    else setData({ status: "loading" });

    fetchPuzzleData()
      .then((fresh) => {
        if (!preview) remember(PUZZLE_KEY, { date: today, lang, data: fresh });
        if (JSON.stringify(fresh) === JSON.stringify(puzzleData)) return;

        // A game already under way keeps the puzzle it started with.
        const started = puzzle && loadGuessedIatas(progressKey(puzzle)).length > 0;
        if (started) return;

        if (puzzle) version += 1;
        puzzle = parsePuzzle(fresh);
        show();
      })
      .catch((error) => {
        // With a remembered puzzle on screen, a failed check changes nothing.
        if (!puzzle) fail(error);
      });

    const airportsAreFresh =
      savedAirports && Date.now() - savedAirports.savedAt < AIRPORTS_FRESH_FOR;
    if (!airportsAreFresh) {
      fetchAirportData()
        .then((fresh) => {
          remember(AIRPORTS_KEY, { savedAt: Date.now(), data: fresh });
          airports = parseAirports(fresh);
          show();
        })
        .catch((error) => {
          if (!airports) fail(error);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);

  return { data, retry };
};
